"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosCustomerUpdateInputSchema = exports.APosCustomerCreateInputSchema = exports.APosCustomerSchema = exports.SavedAddressSchema = exports.APosCustomerSourceSchema = exports.APosCustomerAccountStatusSchema = exports.APosCustomerScentPreferencesSchema = exports.APosB2BStatusSchema = exports.APosCustomerTypeSchema = exports.APosMemberTierSchema = void 0;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../common/timestamps.schema");
const id_schema_1 = require("../../common/id.schema");
const location_schema_1 = require("./location.schema");
exports.APosMemberTierSchema = zod_1.z.enum(['bronze', 'silver', 'gold', 'platinum']);
// Canonical enums — Phase A owns these; C and G import and reuse them.
// One casing: APosB2BStatusSchema (not APosB2bStatusSchema).
// 'contact' (email receipts, 2026-09-17): a marketing contact kept from a POS
// receipt email — NOT a loyalty member. Member search/stats exclude it.
exports.APosCustomerTypeSchema = zod_1.z.enum(['retail', 'b2b', 'contact']);
exports.APosB2BStatusSchema = zod_1.z.enum(['none', 'pending', 'approved', 'rejected']);
// Module 29 — Oil Blending: per-customer scent preferences (optional, additive)
exports.APosCustomerScentPreferencesSchema = zod_1.z.object({
    likes: zod_1.z.array(zod_1.z.string()).default([]),
    dislikes: zod_1.z.array(zod_1.z.string()).default([]),
    notes: zod_1.z.string().max(2000).default(''),
});
// Module 29 — Oil Blending: account status (Phase 3 customer portal claim flow)
exports.APosCustomerAccountStatusSchema = zod_1.z.enum(['guest', 'claimed']);
// Where a customer row came from. Known writers: pos-loyalty-service
// member-create ('customer_display') and pos-sales-service POST /receipts/email
// ('pos_receipt'). Rows written before this enum carry other strings, so the
// field accepts any short string as well.
exports.APosCustomerSourceSchema = zod_1.z.enum(['customer_display', 'pos_receipt']);
// Checkout-loyalty — saved addresses for storefront checkout autofill (additive).
exports.SavedAddressSchema = zod_1.z.object({
    id: zod_1.z.string(),
    label: zod_1.z.string().optional(),
    street: zod_1.z.string().optional(),
    street2: zod_1.z.string().optional(),
    city: zod_1.z.string().optional(),
    state: zod_1.z.string().optional(),
    zip: zod_1.z.string().optional(),
    country: zod_1.z.string().optional(),
    lat: zod_1.z.number().optional(),
    lon: zod_1.z.number().optional(),
    isDefault: zod_1.z.boolean().optional(),
});
exports.APosCustomerSchema = zod_1.z.object({
    tenantId: id_schema_1.TenantIdSchema,
    customerId: id_schema_1.EntityIdSchema,
    phone: zod_1.z.string().min(1).max(30),
    email: zod_1.z.string().email().optional(),
    firstName: zod_1.z.string().max(100).optional(),
    lastName: zod_1.z.string().max(100).optional(),
    fullName: zod_1.z.string().max(200).optional(),
    dateOfBirth: zod_1.z.number().optional(),
    address: location_schema_1.APosAddressSchema.optional(),
    loyaltyPoints: zod_1.z.number().int().nonnegative().default(0),
    memberTier: exports.APosMemberTierSchema.default('bronze'),
    preferredLocationId: id_schema_1.EntityIdSchema.optional(),
    marketingOptIn: zod_1.z.boolean().default(false),
    totalSpent: zod_1.z.number().nonnegative().default(0),
    orderCount: zod_1.z.number().int().nonnegative().default(0),
    averageOrderValue: zod_1.z.number().nonnegative().optional(),
    firstPurchaseDate: zod_1.z.number().optional(),
    lastPurchaseDate: zod_1.z.number().optional(),
    notes: zod_1.z.string().max(2000).optional(),
    // ----- Module 29 (Oil Blending) additive fields — ALL optional, backward-compatible -----
    scentPreferences: exports.APosCustomerScentPreferencesSchema.optional(),
    cognito_user_id: zod_1.z.string().nullable().optional().describe('Phase 3 placeholder'),
    account_status: exports.APosCustomerAccountStatusSchema.default('guest').optional(),
    email_verified_at: zod_1.z.number().nullable().optional().describe('Unix ms — Phase 3'),
    account_claim_token: zod_1.z.string().nullable().optional().describe('Phase 3 one-shot link'),
    // ----- Unified storefront/marketplace additive fields (Phase A) — ALL optional, backward-compatible -----
    // Phase A is the SINGLE OWNER of this set. Phase C only verifies these exist (no redefinition).
    // Phase G adds only b2bAppliedAt / b2bApprovedAt / b2bApprovedBy on top.
    type: exports.APosCustomerTypeSchema.default('retail'),
    b2bStatus: exports.APosB2BStatusSchema.optional(),
    b2bNetTerms: zod_1.z.string().optional(),
    b2bTaxExempt: zod_1.z.boolean().optional(),
    b2bTaxExemptCertUrl: zod_1.z.string().optional(),
    b2bMinOrderValue: zod_1.z.number().nonnegative().optional(),
    b2bCompanyName: zod_1.z.string().max(200).optional(),
    b2bResaleNumber: zod_1.z.string().optional(),
    ageVerified: zod_1.z.boolean().optional(),
    ageVerifiedUntil: zod_1.z.number().optional(),
    lastVerificationId: zod_1.z.string().optional(),
    // ----- Phase G (O02) net-new B2B-workflow fields (b2bAppliedAt/b2bApprovedAt/b2bApprovedBy not in Phase A) -----
    // Reuse Phase A's APosB2BStatusSchema enum; these add only the approval-workflow timestamp/actor fields.
    b2bAppliedAt: zod_1.z.number().optional(),
    b2bApprovedAt: zod_1.z.number().optional(),
    b2bApprovedBy: id_schema_1.EntityIdSchema.optional(),
    b2bRejectionReason: zod_1.z.string().max(1000).optional(),
    // camelCase Cognito link used by the storefront StorefrontUserPool broker (additive; coexists
    // with the snake_case `cognito_user_id` Module 29 placeholder).
    cognitoUserId: zod_1.z.string().optional(),
    // ----- Checkout-loyalty additive field — ALL optional, backward-compatible -----
    addresses: zod_1.z.array(exports.SavedAddressSchema).optional(),
    // ----- Email receipts / marketing contacts additive fields (2026-09-17) — ALL optional -----
    source: zod_1.z.union([exports.APosCustomerSourceSchema, zod_1.z.string().max(50)]).optional(),
    marketingOptInAt: zod_1.z.number().int().optional().describe('Unix ms when marketingOptIn became true'),
    lastSeenAt: zod_1.z.number().int().optional().describe('Unix ms of the latest receipt email to this contact'),
    emailKey: zod_1.z.string().optional().describe('`${tenantId}#${lowercased email}` — EmailIndex hash key; only set when email is non-empty'),
    // ----- Customer Accounts additive fields (2026-10-06) — ALL optional, backward-compatible -----
    // Read onlineAccess ONLY through deriveOnlineAccess() (customerAccounts.ts): rows from before
    // this feature have no value and derive from cognitoUserId / invitedAt.
    rewardsNumber: zod_1.z.string().max(20).optional().describe('XXXX-XXXX, unique per tenant (APosSettings RN# claim row)'),
    onlineAccess: zod_1.z.enum(['none', 'invited', 'active', 'pending_approval', 'disabled']).optional(),
    invitedAt: zod_1.z.number().int().optional(),
    lastInviteAt: zod_1.z.number().int().optional(),
    inviteCount: zod_1.z.number().int().nonnegative().optional(),
    accountSource: zod_1.z.enum(['till', 'online_signup', 'online_checkout', 'import', 'staff']).optional(),
    lastActivityAt: zod_1.z.number().int().optional(),
    onlineApprovedAt: zod_1.z.number().int().optional(),
    onlineApprovedBy: zod_1.z.string().optional(),
    onlineDeclinedReason: zod_1.z.string().max(1000).optional(),
}).merge(timestamps_schema_1.TimestampsSchema);
exports.APosCustomerCreateInputSchema = zod_1.z.object({
    phone: zod_1.z.string().min(1).max(30),
    email: zod_1.z.string().email().optional(),
    firstName: zod_1.z.string().max(100).optional(),
    lastName: zod_1.z.string().max(100).optional(),
    dateOfBirth: zod_1.z.number().optional(),
    address: location_schema_1.APosAddressSchema.optional(),
    preferredLocationId: id_schema_1.EntityIdSchema.optional(),
    marketingOptIn: zod_1.z.boolean().optional(),
    notes: zod_1.z.string().max(2000).optional(),
});
exports.APosCustomerUpdateInputSchema = exports.APosCustomerCreateInputSchema.partial();
//# sourceMappingURL=customer.schema.js.map